<template>
  <div class="timeline-view bg-white rounded-lg shadow overflow-hidden">
    <div class="p-4 border-b flex items-center justify-center gap-2">
      <Button
        icon="pi pi-chevron-left"
        text
        rounded
        size="small"
        aria-label="Previous period"
        @click="goBackward"
      />
      <h3 class="text-lg font-semibold text-surface-800 min-w-[240px] text-center">
        {{ formatMonthTitle(calendarStart) }}
      </h3>
      <Button
        icon="pi pi-chevron-right"
        text
        rounded
        size="small"
        aria-label="Next period"
        @click="goForward"
      />
      <Button
        icon="pi pi-calendar"
        text
        rounded
        size="small"
        aria-label="Go to today"
        class="ml-2"
        @click="goToToday"
      />
    </div>

    <!-- Day axis: day of week row + date row, with weekend highlight -->
    <div class="linear-month-axis flex border-b bg-surface-50 flex-col">
      <div class="flex min-h-[20px]">
        <div
          v-for="d in linearMonthDays"
          :key="'dow-' + d.key"
          class="linear-month-day flex-1 border-r last:border-r-0 text-center text-[10px] py-0.5 font-medium"
          :class="d.isWeekend ? 'bg-amber-100/80 text-amber-900' : 'text-surface-500'"
        >
          {{ d.dayOfWeekShort }}
        </div>
      </div>
      <div class="flex min-h-[28px]">
        <div
          v-for="d in linearMonthDays"
          :key="d.key"
          class="linear-month-day flex-1 border-r last:border-r-0 text-center text-xs py-1"
          :class="[
            d.isToday ? 'bg-primary-100 text-primary-800 font-semibold' : 'text-surface-600',
            d.isWeekend ? 'bg-amber-50/90' : ''
          ]"
        >
          {{ d.dayNum }}
        </div>
      </div>
    </div>

    <!-- Optional single line: available vs not available (greyed/hashed) -->
    <div
      v-if="availabilityByDay && availabilityByDay.length === linearMonthDays.length"
      class="linear-month-availability-summary flex border-b border-surface-200 bg-surface-50/50"
    >
      <div
        v-for="(available, idx) in availabilityByDay"
        :key="'avail-' + idx"
        class="linear-month-day flex-1 border-r last:border-r-0 min-h-[20px] flex items-center justify-center"
        :class="available ? 'availability-cell-available' : 'availability-cell-unavailable'"
      />
    </div>

    <!-- Rows: bar + optional phase bar below -->
    <div class="linear-month-rows overflow-x-auto">
      <div
        v-for="row in rows"
        :key="row.id"
        class="linear-month-row-wrapper border-b last:border-b-0 hover:bg-surface-50/50 cursor-pointer transition-colors"
        @click="onRowClick(row.id)"
      >
        <!-- One div: status-coloured background with phases inside -->
        <div class="linear-month-track flex-1 relative flex min-h-[48px] py-1.5 pr-2 w-full">
          <div
            v-for="d in linearMonthDaysWeekends"
            :key="'weekend-' + row.id + '-' + d.key"
            class="linear-month-weekend absolute top-0 bottom-0 pointer-events-none"
            :style="{ left: (d.index / linearMonthDays.length) * 100 + '%', width: 100 / linearMonthDays.length + '%' }"
          />
          <!-- Status bar (full project span) with phase segments inside -->
          <div
            v-tooltip.top="row.tooltip"
            class="linear-month-bar absolute rounded py-1 px-2 min-w-[60px] overflow-hidden border text-xs font-medium shadow-sm flex flex-col justify-center gap-0"
            :style="{ left: row.leftPct + '%', width: row.widthPct + '%', ...(row.barStyle || {}) }"
          >
            <div class="flex items-center justify-between gap-2 min-w-0 w-full">
              <span class="truncate flex items-center gap-1 min-w-0">
                <i
                  v-if="row.hasClash"
                  class="pi pi-exclamation-triangle text-red-600 flex-shrink-0"
                  :title="row.clashTooltip || 'Clash'"
                />
                {{ row.label }}
              </span>
              <!-- Three-icon system (same as crew widget: unassigned / outstanding / confirmed) -->
              <div
                v-if="row.crewIcons && row.crewIcons.length > 0"
                class="flex items-center gap-1 flex-shrink-0"
              >
                <span
                  v-for="(item, idx) in row.crewIcons"
                  :key="'crew-icon-' + row.id + '-' + idx"
                  v-tooltip.top="item.tooltip || item.label"
                  class="crew-slot-icon inline-flex items-center justify-center rounded border-2 border-current min-w-[28px] h-7 px-3 text-sm"
                  :class="item.slotClass || ''"
                >
                  <i :class="'pi ' + item.icon" />
                  <span
                    v-if="item.count != null && item.count !== ''"
                    class="ml-0.5 text-[10px] font-semibold"
                  >{{ item.count }}</span>
                </span>
              </div>
            </div>
            <!-- Phases glued to bottom, ignoring bar left/right/bottom padding -->
            <div
              v-if="row.phaseBars && row.phaseBars.length > 0"
              class="absolute -left-2 -right-2 -bottom-1 min-h-[10px] rounded-b overflow-hidden"
            >
              <div
                v-for="(pb, pidx) in row.phaseBars"
                :key="'phase-' + row.id + '-' + pidx"
                v-tooltip.top="pb.tooltip"
                class="linear-month-phase-bar absolute rounded-sm min-w-[2px] overflow-hidden border-0 pointer-events-auto"
                :style="phaseBarStyleInBar(pb, row)"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!rows || rows.length === 0"
        class="flex min-h-[120px] items-center justify-center py-12 text-surface-500 text-sm"
      >
        {{ emptyMessage || 'No items in this period' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { addDays, subDays, startOfWeek, endOfWeek, startOfDay, format } from 'date-fns';

const props = defineProps({
  /** Rows to display. Each: { id, label, leftPct, widthPct, barStyle?, tooltip?, phaseBars? } */
  rows: {
    type: Array,
    default: () => []
  },
  /** Current calendar anchor date */
  calendarStart: {
    type: [Date, String],
    required: true
  },
  /** 'day' | 'week' | 'month' */
  viewMode: {
    type: String,
    default: 'month',
    validator: (v) => ['day', 'week', 'month'].includes(v)
  },
  /** Number of days to show in month view */
  monthViewDays: {
    type: Number,
    default: 30
  },
  /** Message when there are no rows */
  emptyMessage: {
    type: String,
    default: ''
  },
  /** Optional: per-day availability for summary row (true = available, false = not). Length must match view days. */
  availabilityByDay: {
    type: Array,
    default: () => null
  }
});

const emit = defineEmits(['update:calendarStart', 'row-click']);

const calendarStartNormalized = computed(() => {
  const v = props.calendarStart;
  if (v instanceof Date) return v;
  const d = typeof v === 'string' ? new Date(v.split('T')[0]) : new Date(v);
  return Number.isNaN(d.getTime()) ? startOfDay(new Date()) : d;
});

const viewStart = computed(() => {
  const c = calendarStartNormalized.value;
  if (props.viewMode === 'day') return startOfDay(c);
  if (props.viewMode === 'week') return startOfWeek(c, { weekStartsOn: 1 });
  return startOfDay(c);
});

const viewEnd = computed(() => {
  const start = viewStart.value;
  if (props.viewMode === 'day') return start;
  if (props.viewMode === 'week') return endOfWeek(calendarStartNormalized.value, { weekStartsOn: 1 });
  return addDays(start, props.monthViewDays - 1);
});

const totalDays = computed(() => {
  if (props.viewMode === 'day') return 1;
  if (props.viewMode === 'week') return 7;
  return props.monthViewDays;
});

const linearMonthDays = computed(() => {
  const start = viewStart.value;
  const count = totalDays.value;
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < count; i++) {
    const date = addDays(start, i);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const dayOfWeek = d.getDay();
    days.push({
      key: format(date, 'yyyy-MM-dd'),
      date,
      dayNum: format(date, 'd'),
      dayOfWeekShort: format(date, 'EEE'),
      isToday: d.getTime() === today.getTime(),
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6
    });
  }
  return days;
});

const linearMonthDaysWeekends = computed(() => {
  return linearMonthDays.value
    .map((d, i) => ({ ...d, index: i }))
    .filter((d) => d.isWeekend);
});

function formatMonthTitle(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (props.viewMode === 'day') {
    return format(startOfDay(d), 'EEEE, d MMM yyyy');
  }
  if (props.viewMode === 'week') {
    const wStart = startOfWeek(d, { weekStartsOn: 1 });
    const wEnd = endOfWeek(d, { weekStartsOn: 1 });
    return `${format(wStart, 'd MMM')} – ${format(wEnd, 'd MMM yyyy')}`;
  }
  const start = startOfDay(d);
  const end = addDays(start, props.monthViewDays - 1);
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
}

function goBackward() {
  const c = calendarStartNormalized.value;
  let next;
  if (props.viewMode === 'day') {
    next = subDays(c, 1);
  } else if (props.viewMode === 'week') {
    next = subDays(c, 7);
  } else {
    next = subDays(c, props.monthViewDays);
  }
  emit('update:calendarStart', next);
}

function goForward() {
  const c = calendarStartNormalized.value;
  let next;
  if (props.viewMode === 'day') {
    next = addDays(c, 1);
  } else if (props.viewMode === 'week') {
    next = addDays(c, 7);
  } else {
    next = addDays(c, props.monthViewDays);
  }
  emit('update:calendarStart', next);
}

function goToToday() {
  const today = new Date();
  let next;
  if (props.viewMode === 'day') {
    next = startOfDay(today);
  } else if (props.viewMode === 'week') {
    next = startOfWeek(today, { weekStartsOn: 1 });
  } else {
    next = startOfDay(today);
  }
  emit('update:calendarStart', next);
}

/** Phase bar position/style relative to the project bar (so phases sit inside the status-coloured bar) */
function phaseBarStyleInBar(pb, row) {
  const barW = row.widthPct || 100;
  const barL = row.leftPct || 0;
  const leftRel = Math.max(0, ((pb.leftPct - barL) / barW) * 100);
  const widthRel = Math.min(100 - leftRel, (pb.widthPct / barW) * 100);
  return {
    left: leftRel + '%',
    width: Math.max(0, widthRel) + '%',
    backgroundColor: pb.color || '#6b7280'
  };
}

function onRowClick(id) {
  emit('row-click', id);
}

defineExpose({
  viewStart,
  viewEnd,
  totalDays,
  linearMonthDays
});
</script>

<style scoped>
.linear-month-axis-days {
  min-width: 400px;
}

.linear-month-track {
  min-width: 400px;
}

.linear-month-bar {
  top: 4px;
  bottom: 4px;
}

.linear-month-phase-bar {
  top: 2px;
  bottom: 2px;
}

.linear-month-weekend {
  background-color: rgb(254 243 199 / 0.4);
}

/* Availability summary row: available = light; not available = greyed + hashed */
.availability-cell-available {
  background-color: rgb(240 253 244 / 0.8);
}

.availability-cell-unavailable {
  background-color: rgb(229 231 235 / 0.9);
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 4px,
    rgb(156 163 175 / 0.4) 4px,
    rgb(156 163 175 / 0.4) 8px
  );
}

/* Three-icon system (matches crew widget): unassigned / outstanding / confirmed – rectangles */
.crew-slot-icon {
  @apply inline-flex items-center justify-center rounded border-2 border-current flex-shrink-0;
}
.crew-slot-empty {
  @apply text-amber-500;
}
.crew-slot-requested {
  @apply text-neutral-500;
}
.crew-slot-confirmed {
  @apply text-green-500;
}
</style>
